import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react'
import t from 'prop-types'
import { useMutation } from '@apollo/client'
import mitt from 'mitt'

import { setToken, removeToken, removeSSOCookie, setEmail } from './store'
import { LOGIN_MUTATION } from './login-mutation'
import { useHistory, useLocation } from 'react-router'
import { logoutDialog } from 'ui'
import {
} from 'routes'

const AuthContext = React.createContext()

export const authEmitter = mitt()

function AuthProvider ({ children }) {
  const { pathname, search } = useLocation()
  const history = useHistory()
  const [loginMutation, { loading }] = useMutation(LOGIN_MUTATION)
  const [didCheckUserIn, setDidCheckUserIn] = useState(false)
  const [userInfo, setUserInfo] = useState({
    isUserLoggedIn: false,
    user: null,
  })

  const checkUser = useCallback(async () => {
    try {
      const ssoToken = await getCookie(process.env.REACT_APP_COOKIE_NAME)
      setToken(ssoToken)
      setUserInfo({ isUserLoggedIn: !!ssoToken, user: null })
    } catch (_) {
      setUserInfo({ isUserLoggedIn: false, user: null })
    }

    setDidCheckUserIn(true)
  }, [setUserInfo, setDidCheckUserIn])

  const login = useCallback(async loginData => {
    const input = {
      ...loginData,
      origin: 'WEB',
      params: {
        urlParams: search,
        formType: 'login',
      },
    }

    const { data } = await loginMutation({ variables: { input } })

    await setToken(data.login)
    await setEmail(loginData.email)

    setUserInfo({
      isUserLoggedIn: true,
      user: {},
    })
  }, [loginMutation, search])

  const logout = useCallback(async () => {
    await removeToken()
    await removeSSOCookie()

    try {
      localStorage.removeItem('auth-storage')
    } catch (error) {
      console.error('Erro ao remover auth-storage:', error)
    }

    sessionStorage.clear()

    setUserInfo({
      isUserLoggedIn: false,
      user: null,
    })

    window.location.href = `${process.env.REACT_APP_BETA_VERSION_URL}/login`
  }, [])

  useEffect(() => {
    checkUser()
  }, [checkUser, logout])

  useEffect(() => {
    authEmitter.on(LOGOUT_EVENT, () => {
      // if (!PUBLIC_PAGES.includes(pathname)) {
      logoutDialog(logout)
      // }
    })
    return () => authEmitter.all.clear()
  }, [history, logout, pathname])

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        userInfo,
        isLogging: loading,
        didCheckUserIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: t.node.isRequired,
}

function useAuth () {
  return useContext(AuthContext)
}

export const LOGOUT_EVENT = 'LOGOUT_EVENT'

export const dispatchLogout = () => authEmitter.emit(LOGOUT_EVENT)

export { AuthProvider, AuthContext, useAuth }

async function getCookie (name) {
  const matches = document.cookie.match(
    new RegExp(
      '(?:^|; )' +
      name.replace(/[.$?*|{}()\\/+^]/g, '\\$&') +
      '=([^;]*)'
    )
  )
  return matches ? decodeURIComponent(matches[1]) : undefined
}

export { getCookie }
